import { InboxOrderByEnum, Maybe, Notification } from './generated/graphql';

import { LE_TIME_ZONE } from '../constants/LeaseEndInfo';
import {
  formatDateNoYearWithTime,
  formatDateWithTime,
  formatPhoneNumberForDisplay,
  formatTime,
  truncateString,
} from '../libs/utils';
import { getMinutesAgo } from '../utils/dates';

import { getFullNameWithInitial } from './customerGql';
import { Deal } from './dealGql';

export interface MediaListData {
  url: string;
  contentType?: string;
}

export interface MediaListObj {
  data: MediaListData[];
}

export interface Message {
  id: string;
  fromCustomer: boolean;
  body: string;
  senderName: string;
  dateCreated?: string;
  dateCreatedTz?: string;
  from: string;
  deals?: Deal[];
  notifications?: Notification[];
  groupKey?: string;
  isFirstInGroup?: boolean;
  mediaListObj?: MediaListObj;
  status?: Maybe<string>;
  errorReason?: Maybe<string>;
}

export const getTimeString = (message: Message) => {
  try {
    const dateCreatedString = message.dateCreated
      ? formatDateWithTime(message.dateCreated, message.dateCreatedTz ?? LE_TIME_ZONE)
      : null;
    const dateCreated = dateCreatedString ? new Date(dateCreatedString) : null;

    const minutesAgo = getMinutesAgo(dateCreated);

    if (minutesAgo !== null && minutesAgo < 60) {
      return minutesAgo <= 1 ? 'Just now' : `${minutesAgo} minutes ago`;
    }

    const isToday = dateCreated ? new Date().toDateString() === dateCreated.toDateString() : false;

    return (
      dateCreated
        ? isToday
          ? formatTime(dateCreated.toISOString())
          : formatDateNoYearWithTime(dateCreated.toISOString())
        : ''
    ).toLowerCase();
  } catch (e) {
    return '';
  }
};

export const getFromString = (message: Message, deal: Deal) => {
  let fullName = '';
  let phoneNumber = '';

  const messageFrom =
    message.from && message.from.startsWith('+1')
      ? formatPhoneNumberForDisplay(message.from)
      : message.from ?? '';
  if (deal.customer?.phone_number && messageFrom === deal.customer.phone_number) {
    fullName = getFullNameWithInitial(deal.customer);
    phoneNumber = deal.customer.phone_number;
  } else if (deal.cobuyer?.phone_number && messageFrom === deal.cobuyer.phone_number) {
    fullName = getFullNameWithInitial(deal.cobuyer);
    phoneNumber = deal.cobuyer.phone_number;
  } else if (deal.contact?.phone_number && messageFrom === deal.contact.phone_number) {
    fullName = getFullNameWithInitial(deal.contact);
    phoneNumber = deal.contact.phone_number;
  } else if (
    deal.second_contact?.phone_number &&
    messageFrom === deal.second_contact.phone_number
  ) {
    fullName = getFullNameWithInitial(deal.second_contact);
    phoneNumber = deal.second_contact.phone_number;
  } else {
    fullName = '';
    phoneNumber = messageFrom;
  }

  return truncateString(`${fullName ? `${fullName} - ` : ''}${phoneNumber}`, 30);
};

export const getGroupKey = (orderBy: InboxOrderByEnum, message: Message) => {
  const isOrderByDeal = orderBy === InboxOrderByEnum.Deal;
  const isOrderByDate =
    orderBy === InboxOrderByEnum.DateNewest || orderBy === InboxOrderByEnum.DateOldest;

  if (isOrderByDeal) {
    const dealId = (message.deals?.length ? message.deals[0] : new Deal()).id as unknown as string;
    return `Deal ${dealId}`;
  }

  const dateCreatedString = message.dateCreated
    ? formatDateWithTime(message.dateCreated, message.dateCreatedTz ?? LE_TIME_ZONE)
    : null;
  const dateCreated = dateCreatedString ? new Date(dateCreatedString) : null;
  const isToday = dateCreated ? new Date().toDateString() === dateCreated.toDateString() : false;
  const isYesterday = dateCreated
    ? new Date(new Date().getTime() - 86400000).toDateString() === dateCreated.toDateString()
    : false;

  if (isOrderByDate) {
    return isToday
      ? 'Today'
      : isYesterday
      ? 'Yesterday'
      : dateCreated
      ? dateCreated.toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
        })
      : '';
  }

  return '';
};
